import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export default function Auth({ session, initialMode = 'login' }) {
  const [authMode, setAuthMode] = useState(initialMode); // 'login' | 'signup' | 'forgot' | 'updatePassword'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authMessage, setAuthMessage] = useState({ type: '', text: '' });

  // Listen for password recovery event to switch mode
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setAuthMode('updatePassword');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setAuthMessage({ type: '', text: '' });
    
    const { error } = await supabase.auth.signInWithPassword({
      email: authEmail,
      password: authPassword,
    });

    if (error) setAuthMessage({ type: 'error', text: error.message });
  };

  const handleGoogleLogin = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setAuthMessage({ type: '', text: '' });

    const { data, error } = await supabase.auth.signUp({
      email: authEmail,
      password: authPassword,
      options: { // Note: 'data' property in options might vary by version but this matches previous App.js
        data: {
          full_name: authName,
        },
      },
    });

    if (error) {
      setAuthMessage({ type: 'error', text: error.message });
    } else if (data.session) {
      setAuthMessage({ type: 'success', text: 'Account created! Please try logging in.' });
    } else {
      setAuthMessage({ type: 'success', text: 'Signup successful! Please check your email.' });
      setAuthMode('login');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setAuthMessage({ type: '', text: '' });

    const { error } = await supabase.auth.resetPasswordForEmail(authEmail, {
      redirectTo: window.location.origin,
    });

    if (error) {
       setAuthMessage({ type: 'error', text: error.message });
    } else {
       setAuthMessage({ type: 'success', text: 'Password reset email sent!' });
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setAuthMessage({ type: '', text: '' });

    const { error } = await supabase.auth.updateUser({
      password: authPassword
    });

    if (error) {
      setAuthMessage({ type: 'error', text: error.message });
    } else {
      setAuthMessage({ type: 'success', text: 'Password updated successfully! Please login.' });
      await supabase.auth.signOut();
      setAuthMode('login');
    }
  };

  const containerStyle = { 
    padding: "20px", 
    fontFamily: "sans-serif", 
    maxWidth: "400px", 
    margin: "50px auto", 
    textAlign: "center", 
    border: "1px solid #ddd", 
    borderRadius: "8px", 
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)" 
  };

  const msgStyle = { 
    padding: "10px", 
    marginBottom: "15px", 
    borderRadius: "4px", 
    backgroundColor: authMessage.type === 'error' ? '#ffebee' : '#e8f5e9',
    color: authMessage.type === 'error' ? '#c62828' : '#2e7d32' 
  };

  const inputStyle = { padding: "10px", borderRadius: "4px", border: "1px solid #ccc" };
  const buttonStyle = { padding: "10px", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" };
  const googleBtnStyle = { ...buttonStyle, backgroundColor: "#d85347", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" };

  if (authMode === 'updatePassword') {
    return (
      <div style={containerStyle}>
        <h1>Update Password</h1>
        {authMessage.text && <div style={msgStyle}>{authMessage.text}</div>}
        <form onSubmit={handleUpdatePassword} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <p>Please enter your new password below.</p>
          <input 
            type="password" 
            placeholder="New Password" 
            value={authPassword} 
            onChange={e => setAuthPassword(e.target.value)} 
            required 
            style={inputStyle}
          />
          <button type="submit" style={{ ...buttonStyle, backgroundColor: "#28a745" }}>
            Update Password
          </button>
        </form>
      </div>
    );
  }

  // Common UI Wrapper
  return (
      <div style={containerStyle}>
          <h1>RunBusiness</h1>
          {authMode === 'login' && <p>Please login to continue.</p>}
          {authMessage.text && <div style={msgStyle}>{authMessage.text}</div>}

          {authMode === 'login' && (
            <form onSubmit={handleEmailLogin} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input 
                type="email" 
                placeholder="Email" 
                value={authEmail} 
                onChange={e => setAuthEmail(e.target.value)} 
                required 
                style={inputStyle}
              />
              <input 
                type="password" 
                placeholder="Password" 
                value={authPassword} 
                onChange={e => setAuthPassword(e.target.value)} 
                required 
                style={inputStyle}
              />
              
              <button type="submit" style={buttonStyle}>Sign In</button>
              
              <div style={{ marginTop: "10px", fontSize: "0.9em" }}>
                <button type="button" onClick={() => setAuthMode('signup')} style={{ background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline" }}>Create an account</button>
                <span style={{ margin: "0 5px" }}>|</span>
                <button type="button" onClick={() => setAuthMode('forgot')} style={{ background: "none", border: "none", color: "#666", cursor: "pointer" }}>Forgot Password?</button>
              </div>

              <div style={{ display: "flex", alignItems: "center", margin: "20px 0" }}>
                <div style={{ flex: 1, height: "1px", backgroundColor: "#ddd" }}></div>
                <span style={{ padding: "0 10px", color: "#666", fontSize: "0.9em" }}>OR</span>
                <div style={{ flex: 1, height: "1px", backgroundColor: "#ddd" }}></div>
              </div>

              <button type="button" onClick={handleGoogleLogin} style={googleBtnStyle}>
                <span style={{ fontWeight: "bold" }}>G</span> Sign in with Google
              </button>
            </form>
          )}

          {authMode === 'signup' && (
            <form onSubmit={handleEmailSignUp} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <h2>Sign Up</h2>
              <input 
                type="text" 
                placeholder="Full Name" 
                value={authName} 
                onChange={e => setAuthName(e.target.value)} 
                required 
                style={inputStyle}
              />
              <input 
                type="email" 
                placeholder="Email" 
                value={authEmail} 
                onChange={e => setAuthEmail(e.target.value)} 
                required 
                style={inputStyle}
              />
              <input 
                type="password" 
                placeholder="Password" 
                value={authPassword} 
                onChange={e => setAuthPassword(e.target.value)} 
                required 
                style={inputStyle}
              />

              <button type="submit" style={{ ...buttonStyle, backgroundColor: "#28a745" }}>Create Account</button>
              <div style={{ marginTop: "10px" }}>
                <button type="button" onClick={() => setAuthMode('login')} style={{ background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline" }}>Back to Login</button>
              </div>

              <div style={{ display: "flex", alignItems: "center", margin: "20px 0" }}>
                <div style={{ flex: 1, height: "1px", backgroundColor: "#ddd" }}></div>
                <span style={{ padding: "0 10px", color: "#666", fontSize: "0.9em" }}>OR</span>
                <div style={{ flex: 1, height: "1px", backgroundColor: "#ddd" }}></div>
              </div>

              <button type="button" onClick={handleGoogleLogin} style={{ ...googleBtnStyle, backgroundColor: "#DB4437" }}>
                <span style={{ fontWeight: "bold" }}>G</span> Sign up with Google
              </button>
            </form>
          )}

          {authMode === 'forgot' && (
            <form onSubmit={handlePasswordReset} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <h2>Reset Password</h2>
              <p style={{ fontSize: "0.9em", color: "#666" }}>Enter your email to receive a password reset link.</p>
              <input 
                type="email" 
                placeholder="Email" 
                value={authEmail} 
                onChange={e => setAuthEmail(e.target.value)} 
                required 
                style={inputStyle}
              />
              <button type="submit" style={{ ...buttonStyle, backgroundColor: "#ffc107", color: "black" }}>
                Send Reset Link
              </button>
              <div style={{ marginTop: "10px" }}>
                <button type="button" onClick={() => setAuthMode('login')} style={{ background: "none", border: "none", color: "#007bff", cursor: "pointer", textDecoration: "underline" }}>Back to Login</button>
              </div>
            </form>
          )}
      </div>
  );
}
